// İSG uzmanı portalı — İzin detayı: Kapsanan Personel (api-contract §14.3/§17.2/§22). Carbon
// referans sayfa (S33 planı §6 Slice 3): kart yığını YERİNE gerçek `Table` — sütunlar
// `WorkPermitPersonnelDto`'dan (`packages/shared/src/api/permits.ts`, **`role` alanı YOK**,
// review'in düzelttiği README yanlış-okuması). Ad hücresi bir bağlantı DEĞİL (gidilecek ayrı bir
// rota yok) — tıklanınca sağdan açılan `Sheet`'i (okuma/detay) açan bir `<button>`. Giriş İzni
// Ver/Geri Al + Kapsamdan Çıkar satırın ortak ⋮ menüsündedir (`RowActionsMenu`; plan §2.6). Çekmece
// (`PersonnelDrawer`, GL5) taşeron Personel listesiyle ORTAKTIR; burada `permit` bağlamıyla açılır.
// Personel + readiness üst `GET /permits/{uuid}` sorgusundan (senkron) — bu panelin kendi yükleniyor
// durumu yok.
//
// **Düzenleme (#29).** Kişi kaydını düzenleme oturumu (`usePersonnelEditor` + `usePersonnelClient`)
// BU panelde yaşar ve çekmeceye geçer; "Düzenle" YALNIZ çekmecededir (hedefini taşeronun kayıt
// listesinden hidrate eder). Satırın ⋮'sine bilinçli olarak eklenmez: o menü kişinin bu izindeki
// yerine (giriş izni, kapsam) dair eylemleri taşır, taşeron kaydındaki kişiye değil.
//
// Araçlar sekmesiyle (`PermitVehiclesPanel.tsx`) tek sütun yapısı (GL #12): ad esnek sütundur,
// mutasyonlar ve onay diyalogları satırda DEĞİL panelde yaşar — satır kalkınca diyalog da
// sökülüp odağı `<body>`'ye düşürmesin. Yalnız "Kapsamdan çıkar" satırı kaldırır → odak "Personel
// Ekle"ye iner (`useRowRemovalFocus`); giriş izni ver/geri al satırı TUTAR, odak o satırda kalır.
// Ver/geri al hata verirse liste tazelenir ve `removeFailed` kurulur: satır yenilemede gerçekten
// kalktıysa (404) odak "Personel Ekle"ye iner.

import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableToolbar,
  Tag,
  type WorkPermitDocumentRequestDto,
  type WorkPermitPersonnelDto,
  awaitsActivation,
  cn,
  countReadyPersonnel,
  formatProgress,
  permitsApi,
  personLabel,
  resolveApiErrorMessage,
  useDocumentTypes,
  usePersonnelEditor,
  useRowRemovalFocus,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SearchX, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permitsKeys } from '../../lib/permitsKeys';
import { usePersonnelClient } from '../../lib/personnelClient';
import { PersonnelDrawer } from '../contractors/PersonnelDrawer';
import type { ReviewSource } from '../documents/DocumentReviewActions';
import { PermitPersonnelAddModal } from './PermitPersonnelAddModal';

interface PermitPersonnelPanelProps {
  permitUuid: string;
  contractorUuid: string;
  /** `detail.canCreatePersonnel === true` (GL6 §4.8) — izin modalındaki "Yeni personel oluştur" için. */
  canCreatePersonnel: boolean;
  personnel: WorkPermitPersonnelDto[];
  /** İznin tipinde istenen belge türleri — `personnel` §27 kayıt kodları (§22). */
  requiredDocuments: string[];
  /**
   * İzne özel ek belge talepleri (§30.10) — satırların "n / m"si tip ∪ bu talepler üzerinden
   * sayılır, istenen belgeler satırı da aynı kümeyi gösterir.
   */
  documentRequests: WorkPermitDocumentRequestDto[];
  /**
   * `personnel`'ı taşıyan izin detayı sonucunun zaman damgaları — AYNI sorgu sonucundan gelir
   * (`PermitVehiclesPanel.source` ile aynı sebep: panel kendi gözlemcisiyle okusaydı yeni damga eski
   * `personnel` prop'uyla buluşur ve bekleyen odak talebini satır kalkmadan düşürürdü).
   */
  source: ReviewSource;
}

export function PermitPersonnelPanel({
  permitUuid,
  contractorUuid,
  canCreatePersonnel,
  personnel,
  requiredDocuments,
  documentRequests,
  source,
}: PermitPersonnelPanelProps) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sheetPersonnel, setSheetPersonnel] = useState<WorkPermitPersonnelDto | null>(null);
  const [toRevoke, setToRevoke] = useState<WorkPermitPersonnelDto | null>(null);
  const [toRemove, setToRemove] = useState<WorkPermitPersonnelDto | null>(null);
  // Son giriş izni verme hatası, kişisiyle. Mutasyon durumu değil: `useMutation` yalnız son
  // `mutate`'i izler (A'nın hatası B'yle kaybolurdu) ve uyarı ilgisiz eylemlerden sonra da kalırdı.
  const [grantError, setGrantError] = useState<{ p: WorkPermitPersonnelDto; err: unknown } | null>(
    null,
  );
  // Sheet kapanınca odağın döneceği düğüm (S33 QA fix — `Sheet.tsx`'in dosya başlığı notu):
  // Radix'in Trigger'sız senaryodaki varsayılanı boşta kalır, biz açan satırın "Ad" düğmesini
  // KENDİMİZ tutup `restoreFocusRef` ile Sheet'e veririz.
  const openerRef = useRef<HTMLElement | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  // Satırların ad düğmeleri ve ⋮ tetikleyicileri — menüden açılan diyaloğun odak dönüş zinciri:
  // ad → ⋮ → "Personel Ekle".
  const nameButtons = useRef(new Map<string, HTMLButtonElement>());
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  // TAM liste (aramayla süzülmüş alt küme DEĞİL): süzülmüş görünümde kaldırılan satır da "gitti" sayılsın.
  const rowKeys = useMemo(() => personnel.map((p) => p.uuid), [personnel]);

  const editor = usePersonnelEditor({
    client: usePersonnelClient(contractorUuid),
    onCloseDrawer: () => setSheetPersonnel(null),
  });

  const readyCount = countReadyPersonnel(personnel);
  // Satırların "m"sinin kümesi (§30.10 readiness birleşimi): tip türleri kendi sırasında, ardından
  // tipte olmayan ek talep türleri talep sırasında; tekilleştirilir.
  const requestedTypes = [
    ...new Set([...requiredDocuments, ...documentRequests.map((r) => r.documentType)]),
  ];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length === 0) {
      return personnel;
    }
    return personnel.filter((p) => {
      const haystack =
        `${p.firstName} ${p.lastName} ${p.personalDataMasked ? '' : (p.emailOrPhone ?? '')}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [personnel, search]);

  // Sheet açıkken alttaki personel listesi (grant/revoke/remove sonrası) değişirse — açık
  // kişinin GÜNCEL satırını Sheet'e ilet (bayat readiness/entry göstermesin).
  const sheetTarget = sheetPersonnel
    ? (personnel.find((p) => p.uuid === sheetPersonnel.uuid) ?? null)
    : null;

  // Kişi drawer açıkken kapsamdan çıktı / geri alındı → boş çekmece göstermeden kapat.
  useEffect(() => {
    if (sheetPersonnel !== null && sheetTarget === null) {
      openerRef.current = addButtonRef.current; // "Personel Ekle" (useRowRemovalFocus inişi), <main> değil
      setSheetPersonnel(null);
    }
  }, [sheetPersonnel, sheetTarget]);

  const focus = useRowRemovalFocus({
    rowKeys,
    stamps: source,
    addButtonRef,
    dialogOpen: toRevoke !== null || toRemove !== null,
    otherSurfaceOpen: addOpen || sheetPersonnel !== null,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
    queryClient.invalidateQueries({ queryKey: permitsKeys.list() });
  }

  // Menü öğesinin kendisi menü kapanınca DOM'dan kalkar; dönüş hedefi satırın adı, yoksa ⋮'si.
  function rememberRow(p: WorkPermitPersonnelDto) {
    focus.rememberOpener(
      nameButtons.current.get(p.uuid) ?? triggers.current.get(p.uuid) ?? addButtonRef.current,
    );
  }

  // Değişken kişinin KENDİSİ: hata metni kişiyi adlandırır, liste yenilenip satır kalksa bile.
  // Hata satırın uzağındaki bir ⋮'den tetiklenir → panel düzeyinde `role="alert"` (aşağıda,
  // `grantError`'dan); bir sonraki "Giriş İzni Ver" seçimi uyarıyı kaldırır.
  const grantMutation = useMutation({
    mutationFn: (p: WorkPermitPersonnelDto) => permitsApi.grantPermitEntry(permitUuid, p.uuid),
    onSuccess: invalidate,
    // 409 PERSONNEL_NOT_READY / 404 PERSONNEL_NOT_FOUND: liste bayat → tazele (satır hazırlığı ya
    // da kapsamı sunucudakiyle hizalansın). Grant satırı KALDIRMAZ ama 404'te tazeleme satırı (ve
    // odaktaki ⋮'yi) söker: `removeFailed` yalnız talep kurar — yenilemede satır gerçekten gittiyse
    // odak "Personel Ekle"ye iner, satır kaldıysa talep düşer ve odak o satırın ⋮'sinde kalır.
    onError: (err, p) => {
      setGrantError({ p, err });
      invalidate();
      focus.removeFailed(p.uuid);
    },
  });
  // Geri alma satırı kaldırmaz → `focus.removed` ÇAĞRILMAZ; diyalog kapanınca odak satırın adına döner.
  const revokeMutation = useMutation({
    mutationFn: (userUuid: string) => permitsApi.revokePermitEntry(permitUuid, userUuid),
    onSuccess: () => {
      invalidate();
      setToRevoke(null);
    },
    // Satır zaten gitmiş olabilir (ör. kişi bu arada kapsamdan çıkarıldı → 404): tazele; hook odağı
    // yalnız satır yenilemede gerçekten kalktıysa "Personel Ekle"ye indirir.
    onError: (_error, userUuid) => {
      invalidate();
      focus.removeFailed(userUuid);
    },
  });
  const removeMutation = useMutation({
    mutationFn: (userUuid: string) => permitsApi.removePermitPersonnel(permitUuid, userUuid),
    onSuccess: (_data, userUuid) => {
      invalidate();
      // Kişi kapsamdan çıktı → diyaloğun kapanış dönüşü artık "Personel Ekle"ye iner.
      focus.removed(userUuid);
      setToRemove(null);
    },
    // 404 PERSONNEL_NOT_FOUND: kişi zaten kapsamda değil (bayat liste) → tazele; satır yenilemede
    // kalkarsa odak talebi kurulu. Diyalog hatayı gösterir.
    onError: (_error, userUuid) => {
      invalidate();
      focus.removeFailed(userUuid);
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <TableToolbar
        summary={
          personnel.length > 0
            ? t('permits.personnel.readySummary', {
                ready: readyCount,
                total: personnel.length,
              })
            : undefined
        }
        search={search}
        onSearchChange={setSearch}
        searchLabel={t('permits.personnel.search')}
        searchPlaceholder={
          personnel.some((p) => p.personalDataMasked)
            ? t('permits.personnel.searchPlaceholderNameOnly')
            : t('permits.personnel.searchPlaceholder')
        }
        onNew={() => setAddOpen(true)}
        newLabel={t('permits.personnel.add')}
        newButtonRef={addButtonRef}
      />

      {/* Gerekli belgeler — README Stage 5 "one 12px muted line in the toolbar, not a boxed
          panel": kutulu panel yerine tek satır muted metin. 16px sol girinti (`carbon:pl-4`,
          S33 QA fix) — toolbar özet metniyle AYNI hizaya oturur (`TableToolbar.tsx`'in kendi
          `carbon:pl-4`'ü). Küme satırların "m"siyle aynıdır: tip türleri, ardından tipte olmayan
          §30.10 ek talep türleri (notlar "Belge talepleri" sekmesinde). */}
      <p className="px-1 text-xs text-muted carbon:pl-4">
        {requestedTypes.length === 0
          ? t('permits.detail.requiredDocumentsEmpty')
          : t('permits.personnel.requiredDocsLine', {
              list: requestedTypes.map((type) => label('personnel', type)).join(', '),
            })}
      </p>

      {/* Katalogda karşılığı olmayan kod (ör. 409 PERSONNEL_NOT_READY) genel hataya değil giriş
          izninin kendi metnine düşer (`ConfirmDialog.errorFallback` ile aynı çözüm). */}
      {grantError ? (
        <Alert tone="danger">
          {`${personLabel(grantError.p)}: ${resolveApiErrorMessage(
            grantError.err,
            t,
            'permits.entry.grantError',
          )}`}
        </Alert>
      ) : null}

      {/* `relative`: başlıktaki `sr-only` metin taşan tabloda kaydırıcıdan kaçıp sayfayı kaydırmasın.
          Personel/Araçlar tek sütun yapısı (#12, esnek ad sütunu tarifi): ad sütunu kalan genişliği
          alır ve kırpılır (≥sm `w-full` + `max-w-0`); düğme `w-fit` → alt çizgi/odak halkası yalnız
          metni kaplar, boş hücreyi değil. <sm sabit 10rem. Diğer sütunlar en dar içerik
          genişliğinde kalır: iki kırmızı etiket birlikte çıkan satırda alt alta dizilir (kabul
          edilen sonuç, GL12 günlüğü). */}
      <Table caption={t('permits.detail.section.personnel')} className="relative">
        <TableHeader>
          <TableRow>
            <TableHead
              className="w-full truncate sm:max-w-0"
              title={t('permits.personnel.col.name')}
            >
              {t('permits.personnel.col.name')}
            </TableHead>
            <TableHead className="text-right">{t('permits.personnel.col.training')}</TableHead>
            <TableHead className="text-right">{t('permits.personnel.col.documents')}</TableHead>
            <TableHead>{t('permits.personnel.col.readiness')}</TableHead>
            <TableHead>{t('permits.personnel.col.siteAccess')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('permits.common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 ? (
            <TableMessageRow colSpan={6}>
              {personnel.length === 0 ? (
                <EmptyState icon={Users} title={t('permits.personnel.empty')} />
              ) : (
                <EmptyState
                  icon={SearchX}
                  title={t('permits.personnel.searchEmpty')}
                  description={t('common.noResults.body')}
                  action={
                    <Button variant="ghost" size="sm" onClick={() => setSearch('')}>
                      {t('common.clearFilters')}
                    </Button>
                  }
                  role="status"
                />
              )}
            </TableMessageRow>
          ) : (
            filtered.map((p) => {
              const label = personLabel(p);
              const granted = p.entryPermittedAt !== null;
              // owner review B3-2 — ready=false iken İKİSİ de eksik OLABİLİR (belge VE eğitim);
              // ikisi de gösterilir, `flex-wrap` dar sütunda ikinci satıra sarar. owner review B4-1 —
              // ikisi de AYNI renkte (`red`): ikisi de sahaya girişi eşit derecede engeller, Sheet'in
              // "Eksik belgeler"/"Eksik eğitimler" rozetleriyle (`PersonnelDrawer.tsx`,
              // `lib/blockingReasons.ts`) AYNI renk — kırmızı = engelliyor, ikinci bir anlam TAŞIMAZ.
              const documentsMissing = !p.requiredDocumentsSatisfied;
              const trainingMissing = p.completedTrainingCount < p.requiredTrainingCount;
              return (
                <TableRow key={p.uuid}>
                  {/* Bu sekmede iletişim sütunu yok → adsız kişide düğme iletişimi gösterir
                      (`personLabel`). İkisi de yoksa boş bir düğme değil, soluk "—". */}
                  <TableCell truncate={false} className="sm:max-w-0">
                    {label ? (
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) {
                            nameButtons.current.set(p.uuid, el);
                          } else {
                            nameButtons.current.delete(p.uuid);
                          }
                        }}
                        onClick={(e) => {
                          openerRef.current = e.currentTarget;
                          setSheetPersonnel(p);
                        }}
                        title={label}
                        className="block w-40 truncate text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-fit sm:max-w-full"
                      >
                        {label}
                      </button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'whitespace-nowrap text-right tnum',
                      p.requiredTrainingCount === 0 && 'text-muted',
                    )}
                  >
                    {formatProgress(p.completedTrainingCount, p.requiredTrainingCount)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'whitespace-nowrap text-right tnum',
                      p.requiredDocumentCount === 0 && 'text-muted',
                    )}
                  >
                    {formatProgress(p.satisfiedDocumentCount, p.requiredDocumentCount)}
                  </TableCell>
                  <TableCell>
                    {p.ready ? (
                      <Tag color="green">{t('permits.personnel.ready')}</Tag>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {documentsMissing ? (
                          <Tag color="red">{t('permits.personnel.readiness.documentsMissing')}</Tag>
                        ) : null}
                        {trainingMissing ? (
                          <Tag color="red">
                            {awaitsActivation(p)
                              ? t('permits.personnel.notActivated')
                              : t('permits.personnel.readiness.trainingMissing')}
                          </Tag>
                        ) : null}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tag color={granted ? 'green' : 'gray'}>
                      {granted
                        ? t('permits.personnel.siteAccess.granted')
                        : t('permits.personnel.siteAccess.notGranted')}
                    </Tag>
                  </TableCell>
                  {/* `w-12 px-2` (S33 QA fix) — `TableHead`'in `w-12`'si tek başına sütunu 48px'e
                      SABİTLEMEZ (`table-layout:auto`): base `px-4` + 32px tetikleyici 64px'e
                      büyürdü; `px-2` + 32px = 48px, header'la eşleşir. */}
                  <TableCell className="w-12 px-2">
                    <RowActionsMenu
                      ariaLabel={t('permits.personnel.action.openMenu', { name: label })}
                      triggerRef={(el) => {
                        if (el) {
                          triggers.current.set(p.uuid, el);
                        } else {
                          triggers.current.delete(p.uuid);
                        }
                      }}
                      items={[
                        granted
                          ? {
                              key: 'revoke',
                              label: t('permits.entry.revoke'),
                              onSelect: () => {
                                revokeMutation.reset();
                                rememberRow(p);
                                setToRevoke(p);
                              },
                            }
                          : {
                              key: 'grant',
                              // GL25 kuralı: devre dışı öğe sebebini KENDİ etiketinde taşır; klavye/
                              // ekran okuyucu yolu Hazırlık sütunudur (Radix devre dışı öğeleri
                              // odaklanamaz, bir açıklama asla okunmazdı).
                              label: p.ready
                                ? t('permits.entry.grant')
                                : t('permits.entry.grantBlocked'),
                              disabled:
                                !p.ready ||
                                (grantMutation.isPending &&
                                  grantMutation.variables?.uuid === p.uuid),
                              // Menü kapanınca Radix odağı bu satırın ⋮'sine döndürür; satır kalır.
                              onSelect: () => {
                                setGrantError(null);
                                grantMutation.mutate(p);
                              },
                            },
                        {
                          key: 'remove',
                          label: t('permits.personnel.action.remove'),
                          destructive: true,
                          onSelect: () => {
                            removeMutation.reset();
                            rememberRow(p);
                            setToRemove(p);
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <PermitPersonnelAddModal
        open={addOpen}
        onOpenChange={setAddOpen}
        permitUuid={permitUuid}
        contractorUuid={contractorUuid}
        coveredUuids={rowKeys}
        canCreate={canCreatePersonnel}
        restoreFocusRef={addButtonRef}
      />

      <PersonnelDrawer
        contractorUuid={contractorUuid}
        context={sheetTarget ? { kind: 'permit', person: sheetTarget, source } : null}
        open={sheetPersonnel !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSheetPersonnel(null);
          }
        }}
        restoreFocusRef={openerRef}
        editor={editor}
      />

      <ConfirmDialog
        open={toRevoke !== null}
        onOpenChange={(open) => !open && setToRevoke(null)}
        title={t('permits.entry.revoke.title')}
        body={t('permits.entry.revoke.body', { name: toRevoke ? personLabel(toRevoke) : '' })}
        confirmLabel={t('permits.entry.revoke.confirm')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={revokeMutation.isPending}
        error={revokeMutation.isError ? revokeMutation.error : undefined}
        onConfirm={() => toRevoke && revokeMutation.mutate(toRevoke.uuid)}
      />
      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title={t('permits.personnel.remove.title')}
        body={t('permits.personnel.remove.body', { name: toRemove ? personLabel(toRemove) : '' })}
        confirmLabel={t('permits.personnel.remove.confirm')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={removeMutation.isPending}
        error={removeMutation.isError ? removeMutation.error : undefined}
        onConfirm={() => toRemove && removeMutation.mutate(toRemove.uuid)}
      />
    </div>
  );
}
